// INFRA.RESILIENCE.1 — Supervision de l'Infra API (modèle PUR, testable).
// Un échec isolé => DEGRADED (jamais OFFLINE d'emblée) ; OFFLINE seulement après
// SUP_THRESHOLD échecs consécutifs ; AUTH_ERROR immédiat et jamais ONLINE.

export const SUP_THRESHOLD = 3;
export const DEGRADED_RETRY_MS = 5000;

export const initialSupervision = {
  status: "CHECKING", failures: 0, lastSuccessAt: null, lastCheckAt: null, lastError: null, failureClass: null,
};

function legacyFailureClass(res) {
  const st = res?.status;
  if (st === 401 || st === 403) return "AUTH_ERROR";
  if (res?.error_type === "not_configured" || res?.error_type === "token_missing_local") return "NOT_CONFIGURED";
  if (st === 504) return "TIMEOUT";
  return "INFRA_UNREACHABLE";
}

// res = payload du proxy backend /infrastructure/health, ou { backendUnreachable: true, error }.
export function classifyHealth(res) {
  if (res?.backendUnreachable) return { ok: false, failureClass: "BACKEND_UNREACHABLE", error: res.error || "Backend SkyCore injoignable" };
  if (res?.ok === true && res?.data?.status === "ok") return { ok: true };
  if (res?.ok === true) return { ok: false, failureClass: "UPSTREAM_ERROR", error: "Réponse inattendue de l'Infra API" };
  return { ok: false, failureClass: res?.failure_class || legacyFailureClass(res), error: res?.error || "Infra API injoignable" };
}

export function supervisionReducer(state, ev) {
  if (ev.type === "success") {
    return { ...state, status: "ONLINE", failures: 0, lastSuccessAt: ev.at, lastCheckAt: ev.at, lastError: null, failureClass: null };
  }
  if (ev.type === "failure") {
    const failures = state.failures + 1;
    let status = failures >= (ev.threshold ?? SUP_THRESHOLD) ? "OFFLINE" : "DEGRADED";
    if (ev.failureClass === "AUTH_ERROR") status = "AUTH_ERROR";
    if (ev.failureClass === "NOT_CONFIGURED") status = "NOT_CONFIGURED";
    return { ...state, status, failures, lastCheckAt: ev.at, lastError: ev.error, failureClass: ev.failureClass };
  }
  return state;
}

export function nextDelayMs(state, intervalMs) {
  if (!intervalMs) return 0;
  return state.status === "DEGRADED" ? Math.min(DEGRADED_RETRY_MS, intervalMs) : intervalMs;
}

const FC_LABEL = {
  BACKEND_UNREACHABLE: "backend SkyCore injoignable",
  INFRA_UNREACHABLE: "serveur injoignable",
  TIMEOUT: "délai dépassé",
  DNS_ERROR: "erreur DNS",
  TLS_ERROR: "erreur TLS",
  UPSTREAM_ERROR: "erreur serveur",
};

export function supervisionView(state, fmt = (x) => x) {
  const last = state.lastSuccessAt ? `Dernière vérification réussie : ${fmt(state.lastSuccessAt)}` : "Aucune vérification réussie";
  const why = FC_LABEL[state.failureClass] || state.failureClass || "";
  switch (state.status) {
    case "ONLINE":
      return { color: "#22C55E", label: "Infra API : connectée", detail: `Le serveur d'infrastructure répond normalement. ${last}.` };
    case "DEGRADED":
      return { color: "#F59E0B", label: `Infra API : vérification en échec (${state.failures}/${SUP_THRESHOLD})`,
        detail: `${why ? `${why} — ` : ""}nouvelle tentative automatique. ${last}.` };
    case "OFFLINE":
      return { color: "#EF4444", label: state.failureClass === "BACKEND_UNREACHABLE" ? "Backend SkyCore : injoignable" : "Infra API : hors ligne",
        detail: `${state.lastError || "Infra API injoignable"} — ${state.failures} échecs consécutifs. ${last}.` };
    case "AUTH_ERROR":
      return { color: "#EF4444", label: "Infra API : authentification refusée", detail: `${state.lastError || "Token invalide"}. ${last}.` };
    case "NOT_CONFIGURED":
      return { color: "#EF4444", label: "Infra API : non configurée", detail: state.lastError || "Configuration manquante côté backend." };
    default:
      return { color: "#525252", label: "Infra API : vérification…", detail: "Interrogation de l'Infra API", pulse: true };
  }
}

// Disjoncteur SSH par Raspberry (GET /infrastructure/resilience) -> vue.
export function raspberryView(hostState, nowMs = Date.now()) {
  const b = hostState?.ssh;
  if (!b) return { color: "#525252", label: "non sondé", state: "unknown" };
  if (b.state === "open") return { color: "#EF4444", label: `injoignable — prochaine vérification dans ${Math.ceil(b.retry_after_s || 0)} s`, state: "offline" };
  if (b.state === "half_open") return { color: "#F59E0B", label: "nouvelle tentative en cours", state: "retrying" };
  if (b.consecutive_failures > 0) return { color: "#F59E0B", label: `échec ponctuel (${b.consecutive_failures})`, state: "degraded" };
  return { color: "#22C55E", label: "joignable", state: "online" };
}
